<template>
  <div class="exam-dictation">
    <section class="panel">
      <h2>Record the visit</h2>
      <p class="purpose">
        We write the SOAP for {{ patientName }} from what you say during the exam.
        Leave this recording running for the whole visit. Leave this tab open.
        A routine step does not need a camera. The note is added to this chart only after you review it.
      </p>

      <p v-if="dictationError" class="form-error">{{ dictationError }}</p>
      <p v-if="recorderError" class="form-error">{{ recorderError }}</p>
      <p v-if="usedDefaultMic && recording" class="mic-note">
        This take is using the browser’s default microphone processing.
      </p>
      <p v-if="capped" class="mic-note">
        This visit reached 45 minutes, so the recording stopped.
      </p>

      <div v-if="(recording || capped) && !activeDraft" class="action-row">
        <p class="recording-label">Recording the visit {{ elapsedLabel }}</p>
        <button type="button" class="btn btn-danger" @click="finishRecording">
          Stop and draft
        </button>
      </div>

      <div v-else-if="!activeDraft" class="action-stack">
        <button
          type="button"
          class="btn btn-primary"
          :disabled="uploading"
          @click="beginRecording"
        >
          {{ uploading ? 'Sending…' : 'Record the visit' }}
        </button>
        <button
          type="button"
          class="btn btn-secondary"
          :disabled="uploading"
          @click="importInput?.click()"
        >
          Import a voice memo
        </button>
        <p class="hint">
          A voice memo is the spoken visit. A video cannot draft this note.
          Leave this tab open while recording. The phone app can stay locked.
        </p>
        <input
          ref="importInput"
          type="file"
          accept="audio/*,.m4a,.mp3,.wav,.aac,.ogg"
          class="hidden-input"
          @change="onImport"
        />
      </div>

      <div v-else-if="activeDraft.status === 'processing'" class="hint">
        {{ draftingLabel }}
      </div>

      <div v-else-if="activeDraft.status === 'failed'" class="action-stack">
        <p class="form-error">{{ activeDraft.error || 'The draft failed.' }}</p>
        <button type="button" class="btn btn-ghost" @click="discardDraft">
          Discard this recording
        </button>
      </div>

      <form v-else class="draft-form" @submit.prevent="saveDraft">
        <p class="hint">
          Edit the draft, then add it to this chart. Mentioned medications stay text on the note.
        </p>
        <label v-for="field in draftFields" :key="field.key" class="draft-field">
          <span>{{ field.label }}</span>
          <textarea v-model="draftEdits[field.key]" rows="3" class="form-control" />
        </label>
        <div class="action-row">
          <button type="submit" class="btn btn-primary" :disabled="saving">
            {{ saving ? 'Saving…' : 'Add to chart' }}
          </button>
          <button
            type="button"
            class="btn btn-ghost"
            :disabled="saving"
            @click="discardDraft"
          >
            Discard
          </button>
        </div>
      </form>
    </section>

    <section v-if="sampleExam" class="panel">
      <h2>Sample visit: {{ sampleExam.title }}</h2>
      <p class="purpose">
        A practice recording for this chart. It shows how a visit becomes a SOAP note.
        Drafting it adds a separate note only after you review it.
      </p>
      <p>{{ sampleExam.what_happened }}</p>
      <dl class="sample-rows">
        <div v-for="row in sampleRows" :key="row.key">
          <dt>{{ row.label }}</dt>
          <dd>{{ sampleExam[row.key] }}</dd>
        </div>
      </dl>
      <details>
        <summary>What you would say into the microphone</summary>
        <p>{{ sampleExam.say_this }}</p>
      </details>
      <button
        type="button"
        class="btn btn-secondary"
        :disabled="uploading || recording || Boolean(activeDraft)"
        @click="draftSample"
      >
        {{ uploading ? 'Sending…' : 'Draft SOAP from this sample' }}
      </button>
    </section>
  </div>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import { useExamRecorder } from '@/composables/useExamRecorder';
import sampleExams from '@/data/sampleExams.json';
import {
  confirmSoapDictation,
  discardSoapDictation,
  getSoapDictation,
  startSoapDictation,
} from '@/services/veterinaryPatients';

const props = defineProps({
  subjectId: { type: [String, Number], required: true },
  patientName: { type: String, default: 'this patient' },
  openDrafts: { type: Array, default: () => [] },
});

const emit = defineEmits(['saved']);

const draftFields = [
  { key: 'subjective', label: 'Subjective' },
  { key: 'objective', label: 'Objective' },
  { key: 'assessment', label: 'Assessment' },
  { key: 'plan', label: 'Plan' },
  { key: 'mentioned_meds', label: 'Medications mentioned' },
  { key: 'transcript', label: 'Transcript' },
];

const sampleRows = [
  { key: 'subjective', label: 'Subjective — what the owner reports' },
  { key: 'objective', label: 'Objective — what the exam finds' },
  { key: 'assessment', label: 'Assessment — the working diagnosis' },
  { key: 'plan', label: 'Plan — what happens next' },
  { key: 'mentioned_meds', label: 'Medications mentioned — text on the note' },
];

const {
  recording,
  elapsedSec,
  recorderError,
  usedDefaultMic,
  capped,
  start: startRecorder,
  stop: stopRecorder,
  cancel: cancelRecorder,
} = useExamRecorder();

const importInput = ref(null);
const uploading = ref(false);
const saving = ref(false);
const dictationError = ref('');
const activeDraft = ref(null);
const draftEdits = ref({});
let pollTimer = null;

const sampleExam = computed(() => {
  const name = props.patientName.trim().toLowerCase();
  return sampleExams.find((exam) => exam.patient_name.toLowerCase() === name) || null;
});

const elapsedLabel = computed(() => {
  const minutes = Math.floor(elapsedSec.value / 60);
  const seconds = String(elapsedSec.value % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
});

function applyDraft(draft) {
  activeDraft.value = draft;
  draftEdits.value = {
    subjective: draft.subjective || '',
    objective: draft.objective || '',
    assessment: draft.assessment || '',
    plan: draft.plan || '',
    mentioned_meds: draft.mentioned_meds || '',
    transcript: draft.transcript || '',
  };
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

const draftingLabel = computed(() => 'Drafting the SOAP from what was said on this visit…');

function startPolling(documentId) {
  stopPolling();
  const started = Date.now();
  const waitMs = 10 * 60 * 1000;
  pollTimer = setInterval(async () => {
    if (Date.now() - started > waitMs) {
      stopPolling();
      dictationError.value = 'The spoken visit did not draft. Open this chart again in a minute.';
      return;
    }
    try {
      const draft = await getSoapDictation(props.subjectId, documentId);
      if (draft.status === 'processing') return;
      stopPolling();
      applyDraft(draft);
    } catch (err) {
      stopPolling();
      dictationError.value = err.response?.data?.error || 'Could not check the draft.';
    }
  }, 2000);
}

async function uploadExam(file, source) {
  dictationError.value = '';
  uploading.value = true;
  try {
    const draft = await startSoapDictation(props.subjectId, file, source);
    applyDraft(draft);
    if (draft.status === 'processing') startPolling(draft.id);
  } catch (err) {
    dictationError.value = err.response?.data?.error
      || err.response?.data?.message
      || 'Could not send this recording.';
  } finally {
    uploading.value = false;
  }
}

async function draftSample() {
  if (!sampleExam.value) return;
  dictationError.value = '';
  uploading.value = true;
  try {
    const response = await fetch(sampleExam.value.audio);
    if (!response.ok) throw new Error('missing sample');
    const blob = await response.blob();
    const file = new File(
      [blob],
      `${sampleExam.value.patient_name}-sample.wav`,
      { type: 'audio/wav' },
    );
    uploading.value = false;
    await uploadExam(file, 'import');
  } catch {
    uploading.value = false;
    dictationError.value = 'The sample recording is not in this build.';
  }
}

async function beginRecording() {
  dictationError.value = '';
  await startRecorder();
}

async function finishRecording() {
  const file = await stopRecorder();
  if (!file || !file.size) {
    dictationError.value = 'The recording was empty.';
    return;
  }
  await uploadExam(file, 'phone');
}

function isVisitVideo(file) {
  const type = (file.type || '').split(';')[0].trim().toLowerCase();
  if (type.startsWith('audio/')) return false;
  if (type.startsWith('video/')) return true;
  return /\.(mp4|mov|m4v)$/i.test(file.name || '');
}

function isStillPhoto(file) {
  const type = (file.type || '').split(';')[0].trim().toLowerCase();
  if (type.startsWith('video/') || type.startsWith('audio/')) return false;
  if (type.startsWith('image/')) return true;
  return /\.(jpe?g|png|gif|webp|heic|heif|tiff?|bmp|dng)$/i.test(file.name || '');
}

async function onImport(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  if (isVisitVideo(file)) {
    dictationError.value = 'A long video cannot draft this visit. Record what was said on the phone. Add a short clip only of what you can see.';
    return;
  }
  if (isStillPhoto(file)) {
    dictationError.value = `A photo has no audio, so it can’t be drafted into a SOAP note for ${props.patientName}. Add a short look at the problem on the phone chart.`;
    return;
  }
  await uploadExam(file, 'import');
}

async function saveDraft() {
  if (!activeDraft.value) return;
  saving.value = true;
  dictationError.value = '';
  try {
    await confirmSoapDictation(
      props.subjectId,
      activeDraft.value.id,
      draftEdits.value,
    );
    activeDraft.value = null;
    emit('saved');
  } catch (err) {
    dictationError.value = err.response?.data?.error || 'Could not add this note to the chart.';
  } finally {
    saving.value = false;
  }
}

async function discardDraft() {
  if (!activeDraft.value) return;
  const id = activeDraft.value.id;
  try {
    await discardSoapDictation(props.subjectId, id);
  } catch (err) {
    dictationError.value = err.response?.data?.error || 'Could not discard this draft.';
    return;
  }
  activeDraft.value = null;
  dictationError.value = '';
}

watch(() => props.openDrafts, (drafts) => {
  const open = drafts?.[0];
  if (open && !activeDraft.value) {
    applyDraft(open);
    if (open.status === 'processing') startPolling(open.id);
  }
}, { immediate: true });

watch(() => props.subjectId, () => {
  stopPolling();
  cancelRecorder();
  activeDraft.value = null;
  dictationError.value = '';
});

onUnmounted(stopPolling);
</script>

<style scoped>
.exam-dictation {
  display: grid;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.purpose,
.hint {
  color: var(--text-muted, #64748b);
  font-size: 0.875rem;
  margin: 0.25rem 0 0.75rem;
}

.form-error {
  color: #b91c1c;
  font-size: 0.875rem;
  margin: 0 0 0.75rem;
}

.mic-note {
  color: #92400e;
  font-size: 0.8125rem;
  margin: 0 0 0.75rem;
}

.action-row,
.action-stack {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}

.action-stack {
  flex-direction: column;
  align-items: flex-start;
}

.recording-label {
  margin: 0;
  font-weight: 600;
}

.draft-form {
  display: grid;
  gap: 0.75rem;
}

.draft-field {
  display: grid;
  gap: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-muted, #64748b);
}

.sample-rows {
  display: grid;
  gap: 0.75rem;
  margin: 0 0 1rem;
}

.sample-rows dt {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted, #64748b);
}

.sample-rows dd {
  margin: 0.15rem 0 0;
}

details {
  margin-bottom: 1rem;
}

.hidden-input {
  display: none;
}

.btn-secondary {
  background: #f1f5f9;
  color: #1e293b;
}

.btn-ghost {
  background: transparent;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-danger {
  background: #dc2626;
  color: #fff;
  border: none;
}
</style>
